import type { CSSProperties, ReactNode } from "react";

/**
 * Barra de progresso simples / proporcional (Design.md §9): preenchimento
 * proporcional usado em "Unidades" (Localizado/Previsto) e "Por fornecedor".
 */
export type ProgressTone = "orange" | "blue" | "green";

const FILL_COLOR: Record<ProgressTone, string> = {
  orange: "var(--orange)",
  blue: "var(--blue)",
  green: "var(--green)"
};

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: ProgressTone;
  label?: ReactNode;
  trailing?: ReactNode;
  height?: number;
  style?: CSSProperties;
}

function clampPercent(value: number, max: number): number {
  if (max <= 0) {
    return 0;
  }
  const pct = (value / max) * 100;
  return Math.max(0, Math.min(100, pct));
}

export function ProgressBar({
  value,
  max = 100,
  tone = "orange",
  label,
  trailing,
  height = 8,
  style
}: ProgressBarProps): ReactNode {
  const percent = clampPercent(value, max);
  const hasHeader = label !== undefined || trailing !== undefined;

  return (
    <div style={{ ...(style ?? {}) }}>
      {hasHeader ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11.5,
            color: "var(--text-muted)",
            marginBottom: "0.3rem"
          }}
        >
          <span>{label}</span>
          <span>{trailing}</span>
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-label={typeof label === "string" ? label : undefined}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height,
          borderRadius: 20,
          background: "var(--panel-alt)",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            borderRadius: 20,
            background: FILL_COLOR[tone],
            transition: "width 0.25s ease"
          }}
        />
      </div>
    </div>
  );
}
